/**
 * LA DROITE D'AJUSTEMENT TRACÉE À L'ŒIL — atelier de figures, palier L5, F7
 * (`docs/pilotage/atelier-figures/CONCEPTION.md` § 3, ligne « Diagramme
 * manipulé, droite d'ajustement à l'œil »).
 *
 * Sur un nuage de points, l'élève déplace les deux poignées d'une droite. Le
 * CODE juge, sans modèle :
 *
 *  - la droite de RÉFÉRENCE est celle de régression (moindres carrés) ou celle
 *    de Mayer, comme l'exercice le déclare ;
 *  - la BANDE : aux deux bords du nuage (la plus petite et la plus grande
 *    abscisse), la droite de l'élève reste à moins de `bande` (en unités de
 *    l'axe des y) de la droite de référence. Tenir aux deux bords, c'est tenir
 *    entre eux : la pente et l'ordonnée à l'origine sont dans la bande ;
 *  - le POINT MOYEN G (x̄ ; ȳ) : la droite passe à moins de `pointMoyen` de lui,
 *    verticalement (défaut : la moitié de la bande).
 *
 * La bande ne se dessine qu'APRÈS la correction (§ 3 : « aucun retour de
 * justesse avant la soumission »). Avant, l'écran ne la connaît pas : elle
 * reste au corrigé, au serveur.
 */
/* ─────────────────────────────── le format ──────────────────────────────── */
export const METHODES_D_AJUSTEMENT = ['regression', 'mayer'];
/* ─────────────────────────────── le calcul ──────────────────────────────── */
export function pointMoyen(points) {
    const n = Math.max(points.length, 1);
    return {
        x: points.reduce((s, p) => s + p.x, 0) / n,
        y: points.reduce((s, p) => s + p.y, 0) / n,
    };
}
/** La droite de régression (moindres carrés). `null` : toutes les abscisses sont égales. */
export function droiteDeRegression(points) {
    const g = pointMoyen(points);
    const sxx = points.reduce((s, p) => s + (p.x - g.x) ** 2, 0);
    if (sxx < 1e-12)
        return null;
    const sxy = points.reduce((s, p) => s + (p.x - g.x) * (p.y - g.y), 0);
    const a = sxy / sxx;
    return { a, b: g.y - a * g.x };
}
/**
 * La droite de MAYER : les points rangés par abscisse, partagés en deux groupes
 * de même effectif (un nombre impair de points : celui du milieu va au PREMIER
 * groupe), et la droite qui passe par les deux points moyens. `null` : les deux
 * points moyens ont la même abscisse.
 */
export function droiteDeMayer(points) {
    const ranges = [...points].sort((p, q) => p.x - q.x);
    const coupe = Math.ceil(ranges.length / 2);
    const g1 = pointMoyen(ranges.slice(0, coupe));
    const g2 = pointMoyen(ranges.slice(coupe));
    if (ranges.length < 2 || Math.abs(g2.x - g1.x) < 1e-12)
        return null;
    const a = (g2.y - g1.y) / (g2.x - g1.x);
    return { droite: { a, b: g1.y - a * g1.x }, g1, g2 };
}
/** La droite que tracent deux poignées. `null` : elles ont la même abscisse (une droite verticale). */
export function droiteDesPoignees(p) {
    if (Math.abs(p.x2 - p.x1) < 1e-9)
        return null;
    const a = (p.y2 - p.y1) / (p.x2 - p.x1);
    return { a, b: p.y1 - a * p.x1 };
}
const en = (d, x) => d.a * x + d.b;
/** La référence d'un nuage ; `null` : le nuage n'en a pas (tous ses points sur une verticale). */
export function referenceDuNuage(points, methode) {
    if (methode === 'mayer') {
        const m = droiteDeMayer(points);
        return m === null ? null : { droite: m.droite, groupes: [m.g1, m.g2] };
    }
    const r = droiteDeRegression(points);
    return r === null ? null : { droite: r };
}
const fr = (v) => String(Math.round(v * 100) / 100)
    .replace('.', ',')
    .replace('-', '−');
/**
 * JUGE une droite tracée à l'œil. Rend `null` quand le nuage n'a pas de
 * référence (l'import le refuse : ce n'est pas l'affaire de l'élève).
 */
export function jugerDroiteALOeil(points, declaration, poignees) {
    const ref = referenceDuNuage(points, declaration.methode);
    if (ref === null)
        return null;
    const g = pointMoyen(points);
    const xs = points.map((p) => p.x);
    const xMin = Math.min(...xs);
    const xMax = Math.max(...xs);
    const eleve = droiteDesPoignees(poignees);
    const tol = 1e-9;
    const dansLaBande = eleve !== null &&
        Math.abs(en(eleve, xMin) - en(ref.droite, xMin)) <= declaration.bande + tol &&
        Math.abs(en(eleve, xMax) - en(ref.droite, xMax)) <= declaration.bande + tol;
    const ecartAuPointMoyen = declaration.pointMoyen ?? declaration.bande / 2;
    const presDuPointMoyen = eleve !== null && Math.abs(en(eleve, g.x) - g.y) <= ecartAuPointMoyen + tol;
    /* Les deux points extrêmes du nuage : la plus petite et la plus grande abscisse. */
    const premier = points.find((p) => p.x === xMin);
    const dernier = points.find((p) => p.x === xMax);
    const pres = Math.max(declaration.bande / 4, 1e-6);
    const parLesExtremes = eleve !== null &&
        premier !== undefined &&
        dernier !== undefined &&
        Math.abs(en(eleve, premier.x) - premier.y) <= pres &&
        Math.abs(en(eleve, dernier.x) - dernier.y) <= pres &&
        !dansLaBande;
    const nomDeLaReference = declaration.methode === 'mayer' ? 'la droite de Mayer' : 'la droite de régression';
    const constats = [];
    if (eleve === null) {
        constats.push({
            etat: 'a_revoir',
            texte: 'Ta droite est verticale. Écarte tes deux poignées de gauche à droite.',
        });
    }
    else {
        constats.push(dansLaBande
            ? { etat: 'tenu', texte: 'Ta droite suit le nuage : elle reste dans la bande.' }
            : parLesExtremes
                ? {
                    etat: 'a_revoir',
                    texte: 'Ta droite passe par les deux points des bouts. Elle doit suivre tout le nuage.',
                }
                : {
                    etat: 'a_revoir',
                    texte: `Ta droite doit rester dans la bande autour de ${nomDeLaReference}.`,
                });
        constats.push(presDuPointMoyen
            ? {
                etat: 'tenu',
                texte: `Ta droite passe près du point moyen G (${fr(g.x)} ; ${fr(g.y)}).`,
            }
            : {
                etat: 'a_revoir',
                texte: `Ta droite doit passer près du point moyen G (${fr(g.x)} ; ${fr(g.y)}).`,
            });
    }
    return {
        tenue: dansLaBande && presDuPointMoyen,
        dansLaBande,
        presDuPointMoyen,
        parLesExtremes,
        eleve,
        reference: ref.droite,
        pointMoyen: g,
        ...(ref.groupes !== undefined ? { groupes: ref.groupes } : {}),
        bande: { xMin, xMax, demiLargeur: declaration.bande },
        constats,
    };
}
/** Ce qu'un schéma ne voit pas d'une droite à l'œil déclarée sur un nuage. */
export function griefsDeDroiteALOeil(points, declaration) {
    const g = [];
    if (points.length < 4)
        g.push('une droite d’ajustement se trace sur un nuage d’au moins 4 points');
    if (referenceDuNuage(points, declaration.methode) === null) {
        g.push('ce nuage n’a pas de droite de référence (ses points sont sur une verticale)');
    }
    if (!(declaration.bande > 0))
        g.push('la bande est un écart strictement positif');
    if (declaration.pointMoyen !== undefined && !(declaration.pointMoyen > 0)) {
        g.push('l’écart au point moyen est strictement positif');
    }
    return g;
}
//# sourceMappingURL=ajustement.js.map